import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import type { Icon } from "@phosphor-icons/react";

// The one HUD button (v2 shape, white): chamfered top-right and bottom-left
// corners with the cut drawn as a hairline, Science Gothic uppercase, a thin
// leading icon. Primary adds L-shaped corner brackets; danger is solid red.
// Every control that looks like a button uses the .btn class this renders,
// including older pages that write className="btn" by hand.

export type ButtonVariant = "default" | "primary" | "danger" | "ghost";
export type ButtonSize = "md" | "sm";

interface Look {
  icon?: Icon;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Only the icon shows; `label` becomes the accessible name. */
  iconOnly?: boolean;
  label?: string;
  block?: boolean;
  children?: ReactNode;
}

export function btnClass({ variant = "default", size = "md", iconOnly, block }: Omit<Look, "icon" | "children" | "label">, extra?: string): string {
  return [
    "btn",
    variant !== "default" ? `btn--${variant}` : null,
    size === "sm" ? "btn--sm" : null,
    iconOnly ? "btn--icon" : null,
    block ? "btn--block" : null,
    extra ?? null,
  ]
    .filter(Boolean)
    .join(" ");
}

function Inner({ icon: IconC, size, iconOnly, label, children }: Look) {
  return (
    <>
      {IconC ? <IconC className="btn__icon" size={size === "sm" ? 14 : 16} weight="light" aria-hidden="true" /> : null}
      {iconOnly ? <span className="sr-only">{label}</span> : <span className="btn__label">{children ?? label}</span>}
    </>
  );
}

export function Button({
  icon,
  variant,
  size,
  iconOnly,
  label,
  block,
  children,
  className,
  type = "button",
  ...rest
}: Look & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">) {
  return (
    <button type={type} className={btnClass({ variant, size, iconOnly, block }, className)} aria-label={iconOnly ? label : rest["aria-label"]} {...rest}>
      <Inner icon={icon} size={size} iconOnly={iconOnly} label={label}>
        {children}
      </Inner>
    </button>
  );
}

/** The same button as a router link. */
export function ButtonLink({
  icon,
  variant,
  size,
  iconOnly,
  label,
  block,
  children,
  className,
  ...rest
}: Look & Omit<LinkProps, "children"> & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "href">) {
  return (
    <Link className={btnClass({ variant, size, iconOnly, block }, className)} {...rest}>
      <Inner icon={icon} size={size} iconOnly={iconOnly} label={label}>
        {children}
      </Inner>
    </Link>
  );
}
